import { useEffect, useState, useRef } from 'react'
import {
  Plus, X, Trash2, Edit2, Check, AlertCircle, Bell, Users, Activity,
  Shield, Building2, Cpu, Briefcase, ChevronDown, ChevronUp, Zap, BookOpen, RefreshCw,
  Send, Paperclip, FileText, Download, Search, UserCheck, ChevronRight, RotateCcw,
  MessageSquare, HardHat, User, Settings, Mail, Phone, Eye, EyeOff, CheckCircle2
} from 'lucide-react'
import api from '../../services/api'

type Tab = 'rules' | 'alerts' | 'contacts' | 'log' | 'settings'
type ContentMode = 'text' | 'doc'
type RecipientMode = 'audience' | 'individual'
type RecipientTab = 'users' | 'contacts' | 'companies'

// ── helpers ───────────────────────────────────────────────────────────────────

const CATEGORY_META: Record<string, { label: string; icon: any; color: string; bg: string }> = {
  regulatory: { label: 'Regulatory Obligations', icon: BookOpen,   color: '#dc2626', bg: '#fef2f2' },
  elt:        { label: 'ELT Notifications',       icon: Building2, color: '#7c3aed', bg: '#f5f3ff' },
  customer:   { label: 'Customer Notifications',  icon: Briefcase, color: '#0369a1', bg: '#eff6ff' },
  business:   { label: 'Business Alerts',         icon: Bell,      color: '#d97706', bg: '#fffbeb' },
  technical:  { label: 'Technical Alerts',        icon: Cpu,       color: '#374151', bg: '#f9fafb' },
}

const CHANNEL_LABEL: Record<string, string> = {
  phone: 'Phone call', written_report: 'Written report', email: 'Email', sms: 'SMS', email_sms: 'Email + SMS',
}

const TRIGGER_LABEL: Record<string, string> = {
  incident_created:          'Incident reported',
  permit_expiring:           'Permit expiring',
  corrective_action_overdue: 'Corrective action overdue',
  competency_expiring:       'Competency expiring',
  contractor_non_compliant:  'Contractor non-compliant',
  system_health:             'System health event',
  audit_overdue:             'Audit overdue',
}

const INCIDENT_TYPE_LABELS: Record<string, string> = {
  fatality: 'Fatality', serious_injury: 'Serious Injury/Illness',
  dangerous_incident: 'Dangerous Incident', near_miss: 'Near Miss',
  property_damage: 'Property Damage', environmental: 'Environmental',
  medical_treatment: 'Medical Treatment',
}

const AUDIENCE_OPTIONS = [
  { value: 'field',        label: 'Field Only',        desc: 'Site workers, operators, frontline staff',       color: '#16a34a', bg: '#f0fdf4' },
  { value: 'company',      label: 'Company Only',      desc: 'Management, office staff, company employees',    color: '#0369a1', bg: '#eff6ff' },
  { value: 'organisation', label: 'Organisation Only', desc: 'All staff across the entire organisation',       color: '#7c3aed', bg: '#f5f3ff' },
  { value: 'all',          label: 'All',               desc: 'All staff plus external notification contacts',  color: '#374151', bg: '#f9fafb' },
]

const LEVEL_BADGE: Record<string, string> = {
  critical: 'badge-critical', high: 'badge-high', medium: 'badge-medium', low: 'badge-low',
}

const STATUS_BADGE: Record<string, string> = {
  sent: 'badge-low', acknowledged: 'badge-low', pending: 'badge-medium', failed: 'badge-critical', queued: 'badge-medium',
}

function formatTimeframe(minutes: number): string {
  if (minutes === 0) return 'Immediate'
  if (minutes < 60) return `${minutes} min`
  if (minutes === 60) return '1 hour'
  if (minutes < 1440) return `${minutes / 60} hours`
  if (minutes === 1440) return '24 hours'
  if (minutes === 2880) return '48 hours'
  if (minutes % 1440 === 0) return `${minutes / 1440} days`
  return `${Math.round(minutes / 60)} hours`
}

function SourceBadge({ source }: { source: string }) {
  const styles: Record<string, { bg: string; color: string }> = {
    regulatory: { bg: '#fef2f2', color: '#dc2626' },
    contractual: { bg: '#eff6ff', color: '#1d4ed8' },
    custom:      { bg: '#f9fafb', color: '#374151' },
  }
  const s = styles[source] || styles.custom
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full capitalize" style={{ background: s.bg, color: s.color }}>{source}</span>
}

function AudienceBadge({ audience }: { audience: string }) {
  const opt = AUDIENCE_OPTIONS.find(a => a.value === audience)
  if (!opt) return <span className="text-xs text-gray-400">{audience}</span>
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: opt.bg, color: opt.color }}>{opt.label}</span>
}

// ── blank forms ────────────────────────────────────────────────────────────────

const BLANK_RULE = {
  name: '', category: 'business', trigger_event: 'incident_created',
  incident_types: [] as string[], severity_levels: [] as string[],
  channel: 'email', timeframe_minutes: 60,
  recipient_type: 'contacts', recipient_name: '', recipient_contact: '',
  timeframe_source: 'custom', regulation_ref: '', jurisdiction: '',
}

const BLANK_CONTACT = {
  name: '', role: '', email: '', mobile: '',
  notify_critical: true, notify_high: true, notify_medium: false, notify_low: false,
  method_email: true, method_sms: false,
  escalation_order: 1, window_minutes: 15, business_hours_only: false,
}

const BLANK_TECH_ALERT = {
  title: '', body: '', audience: 'organisation',
  document_name: '', document_data: '', send_channels: 'email_sms',
}

// ── main component ─────────────────────────────────────────────────────────────

export default function Notifications() {
  const [tab, setTab] = useState<Tab>('rules')
  const [rules, setRules] = useState<any[]>([])
  const [contacts, setContacts] = useState<any[]>([])
  const [log, setLog] = useState<any[]>([])
  const [logTotal, setLogTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState('')
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set())

  // tech alerts
  const [techAlerts, setTechAlerts] = useState<any[]>([])
  const [expandedAlerts, setExpandedAlerts] = useState<Set<string>>(new Set())
  const [showTechModal, setShowTechModal] = useState(false)
  const [techForm, setTechForm] = useState<any>({ ...BLANK_TECH_ALERT })
  const [techSaving, setTechSaving] = useState(false)
  const [techError, setTechError] = useState('')
  const [contentMode, setContentMode] = useState<ContentMode>('text')
  const [recipientMode, setRecipientMode] = useState<RecipientMode>('audience')
  const [recipientTab, setRecipientTab] = useState<RecipientTab>('users')
  const [recipientSearch, setRecipientSearch] = useState('')
  const [selectedRecipients, setSelectedRecipients] = useState<any[]>([])
  const [recipientOptions, setRecipientOptions] = useState<{ users: any[]; contacts: any[]; companies: any[] }>({ users: [], contacts: [], companies: [] })
  const [loadingRecipients, setLoadingRecipients] = useState(false)
  const [editAlertId, setEditAlertId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // rule modal
  const [showRuleModal, setShowRuleModal] = useState(false)
  const [editRule, setEditRule] = useState<any>(null)
  const [ruleForm, setRuleForm] = useState<any>({ ...BLANK_RULE })
  const [ruleSaving, setRuleSaving] = useState(false)
  const [ruleError, setRuleError] = useState('')

  // contact modal
  const [showContactModal, setShowContactModal] = useState(false)
  const [editContact, setEditContact] = useState<any>(null)
  const [contactForm, setContactForm] = useState<any>({ ...BLANK_CONTACT })
  const [contactSaving, setContactSaving] = useState(false)
  const [contactError, setContactError] = useState('')

  // delivery settings
  const [deliveryConfig, setDeliveryConfig] = useState<any>({ sendgrid_configured: false, twilio_configured: false, sendgrid_from_email: '', sendgrid_from_name: '', twilio_phone_number: '' })
  const [emailForm, setEmailForm] = useState({ api_key: '', from_email: '', from_name: '' })
  const [smsForm, setSmsForm] = useState({ account_sid: '', auth_token: '', phone_number: '' })
  const [emailSaving, setEmailSaving] = useState(false)
  const [smsSaving, setSmsSaving] = useState(false)
  const [testEmailAddr, setTestEmailAddr] = useState('')
  const [testPhone, setTestPhone] = useState('')
  const [testingEmail, setTestingEmail] = useState(false)
  const [testingSMS, setTestingSMS] = useState(false)
  const [testingContactId, setTestingContactId] = useState<string | null>(null)
  const [testingContactType, setTestingContactType] = useState<'email' | 'sms' | null>(null)
  const [configError, setConfigError] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)
  const [showAuthToken, setShowAuthToken] = useState(false)
  const [showAccountSid, setShowAccountSid] = useState(false)

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 4500) }

  const buildDeliveryMsg = (d: any): string => {
    if (d.delivery_skipped) return ` to ${d.recipient_count} recipient${d.recipient_count !== 1 ? 's' : ''} (no delivery credentials configured)`
    const parts = []
    if (d.email_sent > 0) parts.push(`${d.email_sent} email${d.email_sent !== 1 ? 's' : ''}`)
    if (d.sms_sent   > 0) parts.push(`${d.sms_sent} SMS`)
    const sent = parts.length ? ` — ${parts.join(', ')} delivered` : ''
    const failed = d.send_failed > 0 ? `, ${d.send_failed} failed` : ''
    return `${sent}${failed}`
  }

  const loadRules      = async () => { try { const { data } = await api.get('/notifications/rules');       setRules(Array.isArray(data) ? data : []) } catch {} }
  const loadContacts   = async () => { try { const { data } = await api.get('/notifications/contacts');    setContacts(Array.isArray(data) ? data : []) } catch {} }
  const loadLog        = async () => { try { const { data } = await api.get('/notifications?limit=50');    setLog(data.data || []); setLogTotal(data.total || 0) } catch {} }
  const loadTechAlerts = async () => { try { const { data } = await api.get('/notifications/tech-alerts'); setTechAlerts(Array.isArray(data) ? data : []) } catch {} }
  const loadConfig     = async () => {
    try {
      const { data } = await api.get('/notifications/config')
      setDeliveryConfig(data)
      setEmailForm({ api_key: '', from_email: data.sendgrid_from_email || '', from_name: data.sendgrid_from_name || '' })
      setSmsForm({ account_sid: '', auth_token: '', phone_number: data.twilio_phone_number || '' })
    } catch {}
  }

  const loadRecipientOptions = async () => {
    if (recipientOptions.users.length) return
    setLoadingRecipients(true)
    try { const { data } = await api.get('/notifications/tech-alerts/options'); setRecipientOptions(data) } catch {}
    setLoadingRecipients(false)
  }

  useEffect(() => {
    setLoading(true)
    Promise.all([loadRules(), loadContacts(), loadLog(), loadTechAlerts(), loadConfig()]).finally(() => setLoading(false))
  }, [])

  const toggleCat = (cat: string) => setCollapsedCats(prev => {
    const next = new Set(prev); next.has(cat) ? next.delete(cat) : next.add(cat); return next
  })

  const toggleAlertExpand = (id: string) => setExpandedAlerts(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next
  })

  // ── tech alert handlers ────────────────────────────────────────────────────

  const openTechModal = (existing?: any) => {
    if (existing) {
      setTechForm({
        title: existing.title || '',
        body: existing.body || '',
        audience: existing.audience || 'organisation',
        document_name: existing.document_name || '',
        document_data: existing.document_data || '',
        send_channels: existing.send_channels || 'email_sms',
      })
      setContentMode(existing.document_data ? 'doc' : 'text')
      setRecipientMode((existing.recipient_mode as RecipientMode) || 'audience')
      const recs = existing.recipients_json
        ? (() => { try { return JSON.parse(existing.recipients_json) } catch { return [] } })()
        : (existing.recipients || [])
      setSelectedRecipients(recs)
      setEditAlertId(existing.id)
    } else {
      setTechForm({ ...BLANK_TECH_ALERT })
      setContentMode('text')
      setRecipientMode('audience')
      setSelectedRecipients([])
      setEditAlertId(null)
    }
    setRecipientSearch('')
    setRecipientTab('users')
    setTechError('')
    setShowTechModal(true)
    if (existing?.recipient_mode === 'individual') loadRecipientOptions()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => setTechForm((f: any) => ({ ...f, document_name: file.name, document_data: ev.target?.result as string }))
    reader.readAsDataURL(file)
  }

  const clearFile = () => {
    setTechForm((f: any) => ({ ...f, document_name: '', document_data: '' }))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const toggleRecipient = (rec: any) => {
    setSelectedRecipients(prev => {
      const exists = prev.find(r => r.id === rec.id && r.type === rec.type)
      return exists ? prev.filter(r => !(r.id === rec.id && r.type === rec.type)) : [...prev, rec]
    })
  }

  const isSelected = (id: string, type: string) => selectedRecipients.some(r => r.id === id && r.type === type)

  const handleTechSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!techForm.title) { setTechError('Title is required'); return }
    if (contentMode === 'text' && !techForm.body) { setTechError('Message body is required'); return }
    if (contentMode === 'doc' && !techForm.document_data) { setTechError('Please attach a document'); return }
    if (recipientMode === 'individual' && selectedRecipients.length === 0) { setTechError('Select at least one recipient'); return }
    setTechSaving(true)
    setTechError('')

    const payload = {
      title: techForm.title,
      body: contentMode === 'text' ? techForm.body : null,
      document_name: contentMode === 'doc' ? techForm.document_name : null,
      document_data: contentMode === 'doc' ? techForm.document_data : null,
      audience: recipientMode === 'audience' ? techForm.audience : null,
      recipient_mode: recipientMode,
      individual_recipients: recipientMode === 'individual' ? selectedRecipients : null,
      send_channels: techForm.send_channels || 'email_sms',
    }

    try {
      let d: any
      if (editAlertId) {
        const res = await api.patch(`/notifications/tech-alerts/${editAlertId}`, payload)
        d = res.data
        const deliveryMsg = buildDeliveryMsg(d)
        showToast(`Alert resent${deliveryMsg}`)
      } else {
        const res = await api.post('/notifications/tech-alerts', payload)
        d = res.data
        const deliveryMsg = buildDeliveryMsg(d)
        showToast(`Alert sent${deliveryMsg}`)
      }
      setShowTechModal(false)
      loadTechAlerts()
    } catch (err: any) {
      setTechError(err.response?.data?.error || 'Failed to send')
    }
    setTechSaving(false)
  }

  // ── delivery settings handlers ─────────────────────────────────────────────

  const handleSaveEmailConfig = async () => {
    setEmailSaving(true); setConfigError('')
    try {
      const payload: any = { sendgrid_from_email: emailForm.from_email, sendgrid_from_name: emailForm.from_name }
      if (emailForm.api_key) payload.sendgrid_api_key = emailForm.api_key
      await api.patch('/notifications/config', payload)
      showToast('Email configuration saved')
      setEmailForm(f => ({ ...f, api_key: '' }))
      loadConfig()
    } catch (err: any) { setConfigError(err.response?.data?.error || 'Failed to save') }
    setEmailSaving(false)
  }

  const handleSaveSMSConfig = async () => {
    setSmsSaving(true); setConfigError('')
    try {
      const payload: any = { twilio_phone_number: smsForm.phone_number }
      if (smsForm.account_sid) payload.twilio_account_sid = smsForm.account_sid
      if (smsForm.auth_token)  payload.twilio_auth_token  = smsForm.auth_token
      await api.patch('/notifications/config', payload)
      showToast('SMS configuration saved')
      setSmsForm(f => ({ ...f, account_sid: '', auth_token: '' }))
      loadConfig()
    } catch (err: any) { setConfigError(err.response?.data?.error || 'Failed to save') }
    setSmsSaving(false)
  }

  const handleTestEmail = async (toAddr: string) => {
    if (!toAddr) return
    setTestingEmail(true); setConfigError('')
    try {
      const { data } = await api.post('/notifications/test', { type: 'email', to_email: toAddr })
      showToast(data.message || 'Test email sent')
    } catch (err: any) { setConfigError(err.response?.data?.error || 'Test email failed') }
    setTestingEmail(false)
  }

  const handleTestSMS = async (toNum: string) => {
    if (!toNum) return
    setTestingSMS(true); setConfigError('')
    try {
      const { data } = await api.post('/notifications/test', { type: 'sms', to_phone: toNum })
      showToast(data.message || 'Test SMS sent')
    } catch (err: any) { setConfigError(err.response?.data?.error || 'Test SMS failed') }
    setTestingSMS(false)
  }

  const handleTestContactEmail = async (c: any) => {
    setTestingContactId(c.id); setTestingContactType('email')
    try {
      const { data } = await api.post('/notifications/test', { type: 'email', to_email: c.email })
      showToast(data.message || `Test email sent to ${c.name}`)
    } catch (err: any) { showToast(`Failed: ${err.response?.data?.error || 'Send error'}`) }
    setTestingContactId(null); setTestingContactType(null)
  }

  const handleTestContactSMS = async (c: any) => {
    setTestingContactId(c.id); setTestingContactType('sms')
    try {
      const { data } = await api.post('/notifications/test', { type: 'sms', to_phone: c.mobile })
      showToast(data.message || `Test SMS sent to ${c.name}`)
    } catch (err: any) { showToast(`Failed: ${err.response?.data?.error || 'Send error'}`) }
    setTestingContactId(null); setTestingContactType(null)
  }

  // ── rule handlers ──────────────────────────────────────────────────────────

  const openAddRule = (defaultCat?: string) => {
    setRuleForm({ ...BLANK_RULE, category: defaultCat || 'business' })
    setEditRule(null); setRuleError(''); setShowRuleModal(true)
  }

  const openEditRule = (r: any) => {
    setRuleForm({
      name: r.name, category: r.category, trigger_event: r.trigger_event,
      incident_types: r.incident_types ? r.incident_types.split(',') : [],
      severity_levels: r.severity_levels ? r.severity_levels.split(',') : [],
      channel: r.channel, timeframe_minutes: r.timeframe_minutes,
      recipient_type: r.recipient_type, recipient_name: r.recipient_name || '',
      recipient_contact: r.recipient_contact || '',
      timeframe_source: r.timeframe_source, regulation_ref: r.regulation_ref || '',
      jurisdiction: r.jurisdiction || '',
    })
    setEditRule(r); setRuleError(''); setShowRuleModal(true)
  }

  const handleRuleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!ruleForm.name) { setRuleError('Name is required'); return }
    setRuleSaving(true); setRuleError('')
    const payload = {
      ...ruleForm,
      incident_types: ruleForm.incident_types.join(',') || null,
      severity_levels: ruleForm.severity_levels.join(',') || null,
    }
    try {
      if (editRule) { await api.patch(`/notifications/rules/${editRule.id}`, payload); showToast('Rule updated') }
      else { await api.post('/notifications/rules', payload); showToast('Rule added') }
      setShowRuleModal(false); loadRules()
    } catch (err: any) { setRuleError(err.response?.data?.error || 'Failed to save') }
    setRuleSaving(false)
  }

  const handleDeleteRule = async (id: string, name: string) => {
    if (!confirm(`Delete rule "${name}"?`)) return
    try { await api.delete(`/notifications/rules/${id}`); showToast('Rule deleted'); loadRules() } catch {}
  }

  const handleToggleRule = async (r: any) => {
    try { await api.patch(`/notifications/rules/${r.id}`, { active: r.active ? 0 : 1 }); loadRules() } catch {}
  }

  const handleSeedDefaults = async () => {
    try {
      const { data } = await api.post('/notifications/rules/defaults')
      showToast(data.seeded === 0 ? 'Defaults already loaded' : `${data.seeded} AU WHS defaults loaded`)
      if (data.seeded > 0) loadRules()
    } catch { showToast('Failed to load defaults') }
  }

  // ── contact handlers ───────────────────────────────────────────────────────

  const openAddContact = () => {
    setContactForm({ ...BLANK_CONTACT, escalation_order: contacts.length + 1 })
    setEditContact(null); setContactError(''); setShowContactModal(true)
  }

  const openEditContact = (c: any) => {
    setContactForm({
      name: c.name, role: c.role || '', email: c.email || '', mobile: c.mobile || '',
      notify_critical: !!c.notify_critical, notify_high: !!c.notify_high,
      notify_medium: !!c.notify_medium, notify_low: !!c.notify_low,
      method_email: !!c.method_email, method_sms: !!c.method_sms,
      escalation_order: c.escalation_order || 1, window_minutes: c.window_minutes || 15,
      business_hours_only: !!c.business_hours_only,
    })
    setEditContact(c); setContactError(''); setShowContactModal(true)
  }

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!contactForm.name) { setContactError('Name is required'); return }
    setContactSaving(true); setContactError('')
    try {
      if (editContact) { await api.patch(`/notifications/contacts/${editContact.id}`, contactForm); showToast('Contact updated') }
      else { await api.post('/notifications/contacts', contactForm); showToast('Contact added') }
      setShowContactModal(false); loadContacts()
    } catch (err: any) { setContactError(err.response?.data?.error || 'Failed to save') }
    setContactSaving(false)
  }

  const handleDeleteContact = async (id: string, name: string) => {
    if (!confirm(`Remove "${name}" from escalation contacts?`)) return
    try { await api.delete(`/notifications/contacts/${id}`); showToast('Contact removed'); loadContacts() } catch {}
  }

  const handleAcknowledge = async (id: string) => {
    try { await api.post(`/notifications/${id}/acknowledge`); showToast('Acknowledged'); loadLog() } catch {}
  }

  if (loading) return <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading...</div>

  // grouped rules
  const grouped: Record<string, any[]> = {}
  for (const r of rules) { if (!grouped[r.category]) grouped[r.category] = []; grouped[r.category].push(r) }
  const CAT_ORDER = ['regulatory', 'elt', 'customer', 'business', 'technical']
  const hasRegDefaults = rules.some(r => r.is_default && r.category === 'regulatory')

  // filtered recipient options for picker
  const q = recipientSearch.toLowerCase()
  const filteredUsers = recipientOptions.users.filter(u =>
    !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.role?.toLowerCase().includes(q)
  )
  const filteredContacts = recipientOptions.contacts.filter(c =>
    !q || c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)
  )
  const filteredCompanies = recipientOptions.companies.filter(c =>
    !q || c.company_name?.toLowerCase().includes(q) || c.contact_name?.toLowerCase().includes(q)
  )

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Notifications</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {rules.length} alert rules · {contacts.length} escalation contacts · {logTotal} sent
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b" style={{ borderColor: 'var(--border)' }}>
        {([
          { id: 'rules'    as Tab, label: 'Alert Rules',         icon: Zap },
          { id: 'alerts'   as Tab, label: 'Send Alert',          icon: Send },
          { id: 'contacts' as Tab, label: 'Escalation Contacts', icon: Users },
          { id: 'log'      as Tab, label: 'Notification Log',    icon: Activity },
          { id: 'settings' as Tab, label: 'Delivery Settings',   icon: Settings },
        ]).map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium"
            style={{
              borderBottom: 'none', background: 'none', border: 'none', cursor: 'pointer',
              borderBottomStyle: 'solid', borderBottomWidth: 2,
              borderBottomColor: tab === t.id ? 'var(--accent)' : 'transparent',
              color: tab === t.id ? 'var(--accent)' : 'var(--text-secondary)',
            }}
          >
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {/* ── ALERT RULES TAB ── */}
      {tab === 'rules' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Configure when and who gets notified — regulatory obligations, ELT escalations, customer, business and technical events.
            </p>
            <div className="flex gap-2">
              {!hasRegDefaults && (
                <button onClick={handleSeedDefaults} className="btn btn-secondary flex items-center gap-1.5" style={{ fontSize: 13 }}>
                  <RefreshCw size={13} /> Load AU WHS Defaults
                </button>
              )}
              <button onClick={() => openAddRule()} className="btn btn-primary flex items-center gap-1.5" style={{ fontSize: 13 }}>
                <Plus size={14} /> Add Rule
              </button>
            </div>
          </div>

          {rules.length === 0 && (
            <div className="card p-8 text-center">
              <Shield size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
              <p className="font-semibold mb-1" style={{ color: 'var(--text)' }}>No alert rules configured</p>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>Start with AU WHS regulatory defaults, then add ELT, customer and business rules.</p>
              <button onClick={handleSeedDefaults} className="btn btn-primary mx-auto flex items-center gap-2">
                <RefreshCw size={15} /> Load AU WHS Defaults
              </button>
            </div>
          )}

          {CAT_ORDER.filter(cat => grouped[cat]?.length > 0).map(cat => {
            const meta = CATEGORY_META[cat] || CATEGORY_META.business
            const catRules = grouped[cat] || []
            const collapsed = collapsedCats.has(cat)
            return (
              <div key={cat} className="card p-0 overflow-hidden">
                <div
                  className="flex items-center justify-between px-5 py-3.5 cursor-pointer"
                  style={{ background: meta.bg, borderBottom: collapsed ? 'none' : '1px solid var(--border)' }}
                  onClick={() => toggleCat(cat)}
                >
                  <div className="flex items-center gap-2.5">
                    <meta.icon size={16} style={{ color: meta.color }} />
                    <span className="font-semibold text-sm" style={{ color: meta.color }}>{meta.label}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ background: meta.color }}>{catRules.length}</span>
                    {cat === 'regulatory' && (
                      <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ background: '#dc2626', opacity: 0.8 }}>AU WHS Act 2011</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    {cat === 'technical' && (
                      <button onClick={() => openTechModal()} className="text-xs px-2.5 py-1 rounded-lg flex items-center gap-1 font-medium text-white" style={{ background: meta.color }}>
                        <Send size={11} /> Send Alert
                      </button>
                    )}
                    <button onClick={() => openAddRule(cat)} className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1" style={{ borderColor: meta.color, color: meta.color, background: 'white' }}>
                      <Plus size={11} /> Add Rule
                    </button>
                    <button onClick={() => toggleCat(cat)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                      {collapsed ? <ChevronDown size={16} style={{ color: meta.color }} /> : <ChevronUp size={16} style={{ color: meta.color }} />}
                    </button>
                  </div>
                </div>

                {/* Sent alerts history for technical category */}
                {!collapsed && cat === 'technical' && techAlerts.length > 0 && (
                  <div style={{ borderBottom: '1px solid var(--border)' }}>
                    <div className="flex items-center justify-between px-5 py-2" style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)' }}>
                      <span className="text-xs font-semibold tracking-wide" style={{ color: 'var(--text-secondary)' }}>SENT ALERTS HISTORY</span>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{techAlerts.length} sent — click to expand</span>
                    </div>
                    {techAlerts.map(ta => {
                      const expanded = expandedAlerts.has(ta.id)
                      const recipients: any[] = (() => { try { return ta.recipients_json ? JSON.parse(ta.recipients_json) : [] } catch { return [] } })()
                      return (
                        <div key={ta.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          {/* Collapsed row */}
                          <div
                            className="flex items-center gap-4 px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleAlertExpand(ta.id)}
                          >
                            <ChevronRight
                              size={15}
                              style={{ color: 'var(--text-muted)', flexShrink: 0, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm truncate" style={{ color: 'var(--text)' }}>{ta.title}</span>
                                {ta.document_name && (
                                  <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: '#f1f5f9', color: '#64748b' }}>
                                    <FileText size={10} /> {ta.document_name}
                                  </span>
                                )}
                                {(ta.resend_count > 0) && (
                                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#fef3c7', color: '#92400e' }}>
                                    Resent ×{ta.resend_count}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-0.5">
                                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                  {new Date(ta.created_at).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })}
                                </span>
                                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>by {ta.sent_by_name}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {ta.recipient_mode === 'individual'
                                ? <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#f0f9ff', color: '#0369a1' }}>
                                    <UserCheck size={10} className="inline mr-1" />{ta.recipient_count} selected
                                  </span>
                                : <AudienceBadge audience={ta.audience} />
                              }
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{ta.recipient_count} recipients</span>
                            </div>
                          </div>

                          {/* Expanded detail */}
                          {expanded && (
                            <div className="px-10 py-4 space-y-4" style={{ background: '#fafafa', borderTop: '1px solid var(--border)' }}>
                              {ta.body && (
                                <div>
                                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>MESSAGE</p>
                                  <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text)' }}>{ta.body}</p>
                                </div>
                              )}
                              {ta.document_name && (
                                <div>
                                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>ATTACHMENT</p>
                                  <a
                                    href={`/api/notifications/tech-alerts/${ta.id}/download`}
                                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm"
                                    style={{ borderColor: 'var(--border)', color: 'var(--accent)', background: 'white' }}
                                  >
                                    <Download size={14} /> {ta.document_name}
                                  </a>
                                </div>
                              )}

                              {/* Recipients detail */}
                              <div>
                                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                                  RECIPIENTS ({ta.recipient_count})
                                </p>
                                {ta.recipient_mode === 'individual' && recipients.length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {recipients.map((r: any, i: number) => (
                                      <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs" style={{ background: 'white', borderColor: 'var(--border)' }}>
                                        {r.type === 'user'    && <User    size={11} style={{ color: '#0369a1' }} />}
                                        {r.type === 'contact' && <Bell    size={11} style={{ color: '#7c3aed' }} />}
                                        {r.type === 'company' && <HardHat size={11} style={{ color: '#d97706' }} />}
                                        <span style={{ color: 'var(--text)' }}>{r.name}</span>
                                        {r.email && <span style={{ color: 'var(--text-muted)' }}>· {r.email}</span>}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <AudienceBadge audience={ta.audience} />
                                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                      {ta.recipient_count} {ta.audience === 'all' ? 'staff + contacts' : 'staff'} notified
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Delivery stats */}
                              {(ta.email_sent > 0 || ta.sms_sent > 0 || ta.send_failed > 0) && (
                                <div className="flex flex-wrap gap-2">
                                  {ta.email_sent > 0 && (
                                    <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: '#f0fdf4', color: '#16a34a' }}>
                                      <Mail size={11} /> {ta.email_sent} email{ta.email_sent !== 1 ? 's' : ''} delivered
                                    </span>
                                  )}
                                  {ta.sms_sent > 0 && (
                                    <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                                      <Phone size={11} /> {ta.sms_sent} SMS delivered
                                    </span>
                                  )}
                                  {ta.send_failed > 0 && (
                                    <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: '#fef2f2', color: '#dc2626' }}>
                                      <AlertCircle size={11} /> {ta.send_failed} failed
                                    </span>
                                  )}
                                </div>
                              )}

                              <div className="flex gap-2 pt-1">
                                <button
                                  onClick={() => openTechModal(ta)}
                                  className="btn btn-primary flex items-center gap-1.5"
                                  style={{ fontSize: 13 }}
                                >
                                  <RotateCcw size={13} /> Modify & Resend
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}

                {!collapsed && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                        <th className="text-left px-4 py-2.5 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Rule</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Trigger</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Timeframe</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Channel</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Recipient</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Source</th>
                        <th className="text-center px-4 py-2.5 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Active</th>
                        <th className="px-4 py-2.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {catRules.map(r => (
                        <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', opacity: r.active ? 1 : 0.5 }}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-sm" style={{ color: 'var(--text)' }}>{r.name}</div>
                            {r.regulation_ref && <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{r.regulation_ref}</div>}
                            {r.incident_types && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {r.incident_types.split(',').map((t: string) => (
                                  <span key={t} className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#f1f5f9', color: '#475569' }}>
                                    {INCIDENT_TYPE_LABELS[t] || t}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {TRIGGER_LABEL[r.trigger_event] || r.trigger_event}
                            {r.severity_levels && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {r.severity_levels.split(',').map((s: string) => (
                                  <span key={s} className={`badge ${LEVEL_BADGE[s] || ''} capitalize`} style={{ fontSize: 10, padding: '1px 5px' }}>{s}</span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-sm" style={{ color: r.timeframe_minutes <= 60 ? 'var(--red)' : r.timeframe_minutes <= 480 ? '#d97706' : 'var(--text)' }}>
                              {formatTimeframe(r.timeframe_minutes)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs capitalize" style={{ color: 'var(--text-secondary)' }}>
                            {CHANNEL_LABEL[r.channel] || r.channel}
                          </td>
                          <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                            <div>{r.recipient_name || r.recipient_type}</div>
                            {r.recipient_contact && <div style={{ color: 'var(--text-muted)' }}>{r.recipient_contact}</div>}
                          </td>
                          <td className="px-4 py-3"><SourceBadge source={r.timeframe_source} /></td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleToggleRule(r)}
                              className="w-9 h-5 rounded-full transition-all relative"
                              style={{ background: r.active ? 'var(--accent)' : '#d1d5db' }}
                            >
                              <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all" style={{ left: r.active ? '1.1rem' : '0.1rem' }} />
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1.5 justify-end">
                              <button onClick={() => openEditRule(r)} className="btn btn-secondary" style={{ padding: '3px 8px', fontSize: 11 }}><Edit2 size={12} /></button>
                              <button onClick={() => handleDeleteRule(r.id, r.name)} className="btn btn-secondary" style={{ padding: '3px 8px', fontSize: 11, color: 'var(--red)' }}><Trash2 size={12} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )
          })}

          {/* Quick add/send buttons for missing categories */}
          {rules.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {CAT_ORDER.filter(cat => !grouped[cat]?.length).map(cat => {
                const meta = CATEGORY_META[cat] || CATEGORY_META.business
                return (
                  <div key={cat} className="flex gap-1">
                    <button onClick={() => openAddRule(cat)} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border" style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)', background: 'white' }}>
                      <meta.icon size={14} /> Add {meta.label}
                    </button>
                    {cat === 'technical' && (
                      <button onClick={() => openTechModal()} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg text-white" style={{ background: meta.color }}>
                        <Send size={13} /> Send Tech Alert
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── SEND ALERT TAB ── */}
      {tab === 'alerts' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Send a custom email and/or SMS broadcast to staff, escalation contacts, or individual recipients.
              </p>
            </div>
            <button onClick={() => openTechModal()} className="btn btn-primary flex items-center gap-1.5">
              <Send size={15} /> New Alert
            </button>
          </div>

          {techAlerts.length === 0 ? (
            <div className="card text-center py-16">
              <Send size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
              <p className="font-medium mb-1" style={{ color: 'var(--text)' }}>No alerts sent yet</p>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>Send a custom email or SMS alert to your team</p>
              <button onClick={() => openTechModal()} className="btn btn-primary mx-auto flex items-center gap-2">
                <Send size={15} /> Send First Alert
              </button>
            </div>
          ) : (
            <div className="card p-0 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3" style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)' }}>
                <span className="text-xs font-semibold tracking-wide" style={{ color: 'var(--text-secondary)' }}>SENT ALERTS — {techAlerts.length} total</span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Click a row to expand</span>
              </div>
              {techAlerts.map(ta => {
                const expanded = expandedAlerts.has(ta.id)
                const recipients: any[] = (() => { try { return ta.recipients_json ? JSON.parse(ta.recipients_json) : [] } catch { return [] } })()
                return (
                  <div key={ta.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <div
                      className="flex items-center gap-4 px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleAlertExpand(ta.id)}
                    >
                      <ChevronRight size={15} style={{ color: 'var(--text-muted)', flexShrink: 0, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate" style={{ color: 'var(--text)' }}>{ta.title}</span>
                          {ta.document_name && (
                            <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: '#f1f5f9', color: '#64748b' }}>
                              <FileText size={10} /> {ta.document_name}
                            </span>
                          )}
                          {ta.resend_count > 0 && (
                            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#fef3c7', color: '#92400e' }}>Resent ×{ta.resend_count}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {new Date(ta.created_at).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })}
                          </span>
                          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>by {ta.sent_by_name}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {ta.email_sent > 0 && (
                          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#f0fdf4', color: '#16a34a' }}>
                            <Mail size={10} /> {ta.email_sent}
                          </span>
                        )}
                        {ta.sms_sent > 0 && (
                          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                            <Phone size={10} /> {ta.sms_sent}
                          </span>
                        )}
                        {ta.recipient_mode === 'individual'
                          ? <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#f0f9ff', color: '#0369a1' }}>
                              <UserCheck size={10} className="inline mr-1" />{ta.recipient_count} selected
                            </span>
                          : <AudienceBadge audience={ta.audience} />
                        }
                      </div>
                    </div>

                    {expanded && (
                      <div className="px-10 py-4 space-y-4" style={{ background: '#fafafa', borderTop: '1px solid var(--border)' }}>
                        {ta.body && (
                          <div>
                            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>MESSAGE</p>
                            <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text)' }}>{ta.body}</p>
                          </div>
                        )}
                        {ta.document_name && (
                          <div>
                            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>ATTACHMENT</p>
                            <a href={`/api/notifications/tech-alerts/${ta.id}/download`}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm"
                              style={{ borderColor: 'var(--border)', color: 'var(--accent)', background: 'white' }}>
                              <Download size={14} /> {ta.document_name}
                            </a>
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>RECIPIENTS ({ta.recipient_count})</p>
                          {ta.recipient_mode === 'individual' && recipients.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {recipients.map((r: any, i: number) => (
                                <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs" style={{ background: 'white', borderColor: 'var(--border)' }}>
                                  {r.type === 'user'    && <User    size={11} style={{ color: '#0369a1' }} />}
                                  {r.type === 'contact' && <Bell    size={11} style={{ color: '#7c3aed' }} />}
                                  {r.type === 'company' && <HardHat size={11} style={{ color: '#d97706' }} />}
                                  <span style={{ color: 'var(--text)' }}>{r.name}</span>
                                  {r.email && <span style={{ color: 'var(--text-muted)' }}>· {r.email}</span>}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <AudienceBadge audience={ta.audience} />
                              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {ta.recipient_count} {ta.audience === 'all' ? 'staff + contacts' : 'staff'} notified
                              </span>
                            </div>
                          )}
                        </div>
                        {(ta.email_sent > 0 || ta.sms_sent > 0 || ta.send_failed > 0) && (
                          <div className="flex flex-wrap gap-2">
                            {ta.email_sent > 0 && (
                              <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: '#f0fdf4', color: '#16a34a' }}>
                                <Mail size={11} /> {ta.email_sent} email{ta.email_sent !== 1 ? 's' : ''} delivered
                              </span>
                            )}
                            {ta.sms_sent > 0 && (
                              <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                                <Phone size={11} /> {ta.sms_sent} SMS delivered
                              </span>
                            )}
                            {ta.send_failed > 0 && (
                              <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: '#fef2f2', color: '#dc2626' }}>
                                <AlertCircle size={11} /> {ta.send_failed} failed
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex gap-2 pt-1">
                          <button onClick={() => openTechModal(ta)} className="btn btn-primary flex items-center gap-1.5" style={{ fontSize: 13 }}>
                            <RotateCcw size={13} /> Modify & Resend
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── ESCALATION CONTACTS TAB ── */}
      {tab === 'contacts' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="card p-4 flex-1 mr-4" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <p className="text-sm font-medium" style={{ color: '#1d4ed8' }}>
                Escalation contacts receive automated notifications when incidents, permits or corrective actions need attention.
                They are called in escalation order, with the next contact notified if no response within the configured window.
              </p>
            </div>
            <button onClick={openAddContact} className="btn btn-primary flex items-center gap-1.5">
              <Plus size={15} /> Add Contact
            </button>
          </div>

          {contacts.length === 0 ? (
            <div className="card text-center py-16">
              <Users size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
              <p className="font-medium mb-1" style={{ color: 'var(--text)' }}>No escalation contacts configured</p>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>Add contacts to receive automated incident and permit alerts</p>
              <button onClick={openAddContact} className="btn btn-primary mx-auto flex items-center gap-2"><Plus size={15} /> Add First Contact</button>
            </div>
          ) : (
            <div className="card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                    <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Order</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Name / Role</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Contact</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Notify For</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Methods</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Response Window</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {contacts.sort((a, b) => (a.escalation_order || 0) - (b.escalation_order || 0)).map(c => (
                    <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td className="px-4 py-3">
                        <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white inline-flex" style={{ background: 'var(--accent)' }}>
                          {c.escalation_order || 1}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium" style={{ color: 'var(--text)' }}>{c.name}</div>
                        {c.role && <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{c.role}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {c.email && <div>{c.email}</div>}
                        {c.mobile && <div>{c.mobile}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {c.notify_critical ? <span className="badge badge-critical" style={{ fontSize: 10 }}>Critical</span> : null}
                          {c.notify_high     ? <span className="badge badge-high"     style={{ fontSize: 10 }}>High</span>     : null}
                          {c.notify_medium   ? <span className="badge badge-medium"   style={{ fontSize: 10 }}>Medium</span>   : null}
                          {c.notify_low      ? <span className="badge badge-low"      style={{ fontSize: 10 }}>Low</span>      : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {[c.method_email && 'Email', c.method_sms && 'SMS'].filter(Boolean).join(' + ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                        {c.window_minutes ? `${c.window_minutes} min` : '—'}
                        {c.business_hours_only ? ' · Bus. hrs' : ''}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5 justify-end flex-wrap">
                          {c.email && (
                            <button
                              onClick={() => handleTestContactEmail(c)}
                              disabled={testingContactId === c.id}
                              className="btn btn-secondary flex items-center gap-1"
                              style={{ padding: '3px 8px', fontSize: 11, color: '#0369a1' }}
                              title={`Send test email to ${c.email}`}
                            >
                              <Mail size={11} />
                              {testingContactId === c.id && testingContactType === 'email' ? '...' : 'Test'}
                            </button>
                          )}
                          {c.mobile && (
                            <button
                              onClick={() => handleTestContactSMS(c)}
                              disabled={testingContactId === c.id}
                              className="btn btn-secondary flex items-center gap-1"
                              style={{ padding: '3px 8px', fontSize: 11, color: '#7c3aed' }}
                              title={`Send test SMS to ${c.mobile}`}
                            >
                              <Phone size={11} />
                              {testingContactId === c.id && testingContactType === 'sms' ? '...' : 'Test'}
                            </button>
                          )}
                          <button onClick={() => openEditContact(c)} className="btn btn-secondary" style={{ padding: '3px 8px', fontSize: 11 }}><Edit2 size={12} /></button>
                          <button onClick={() => handleDeleteContact(c.id, c.name)} className="btn btn-secondary" style={{ padding: '3px 8px', fontSize: 11, color: 'var(--red)' }}><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── NOTIFICATION LOG TAB ── */}
      {tab === 'log' && (
        log.length === 0 ? (
          <div className="card text-center py-16">
            <Activity size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
            <p className="font-medium" style={{ color: 'var(--text)' }}>No notifications sent yet</p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Notifications appear here once incidents trigger rules</p>
          </div>
        ) : (
          <div className="card p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                  <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Sent</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Incident</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Recipient</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Severity</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Channel</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-secondary)' }}>Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {log.map(n => (
                  <tr key={n.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                      {new Date(n.created_at).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>{n.incident_number || '—'}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{n.recipient_name || '—'}</td>
                    <td className="px-4 py-3">
                      {n.severity && <span className={`badge ${LEVEL_BADGE[n.severity] || ''} capitalize`}>{n.severity}</span>}
                    </td>
                    <td className="px-4 py-3 text-xs capitalize" style={{ color: 'var(--text-secondary)' }}>
                      {CHANNEL_LABEL[n.type] || n.type || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge ${STATUS_BADGE[n.status] || ''} capitalize`}>{n.status || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {n.status === 'sent' && (
                        <button onClick={() => handleAcknowledge(n.id)} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 11 }}>
                          <Check size={12} /> Ack
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── DELIVERY SETTINGS TAB ── */}
      {tab === 'settings' && (
        <div className="space-y-6 max-w-2xl">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Configure SendGrid for email delivery and Twilio for SMS. Credentials are stored securely per organisation. Use the test buttons to verify delivery before going live.
          </p>

          {configError && (
            <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: '#fef2f2', color: 'var(--red)' }}>
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{configError}
            </div>
          )}

          {/* Email config */}
          <div className="card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Mail size={18} style={{ color: '#0369a1' }} />
                <h3 className="font-semibold" style={{ color: 'var(--text)' }}>Email Notifications (SendGrid)</h3>
              </div>
              {deliveryConfig.sendgrid_configured
                ? <span className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: '#f0fdf4', color: '#16a34a' }}><CheckCircle2 size={12} /> Configured</span>
                : <span className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: '#fef2f2', color: '#dc2626' }}><AlertCircle size={12} /> Not configured</span>
              }
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                SendGrid API Key {deliveryConfig.sendgrid_configured && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(leave blank to keep existing)</span>}
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={emailForm.api_key}
                  onChange={e => setEmailForm(f => ({ ...f, api_key: e.target.value }))}
                  placeholder={deliveryConfig.sendgrid_configured ? '••••••••••••••••••••' : 'SG.xxxxxxxxxxxxxxxxxxxxxxxx'}
                  style={{ paddingRight: 40 }}
                />
                <button type="button" onClick={() => setShowApiKey(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  {showApiKey ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>From Email</label>
                <input type="email" value={emailForm.from_email} onChange={e => setEmailForm(f => ({ ...f, from_email: e.target.value }))} placeholder="notifications@yourcompany.com.au" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>From Name</label>
                <input value={emailForm.from_name} onChange={e => setEmailForm(f => ({ ...f, from_name: e.target.value }))} placeholder="HSEQ Intelligence" />
              </div>
            </div>

            <button onClick={handleSaveEmailConfig} disabled={emailSaving} className="btn btn-primary" style={{ fontSize: 13 }}>
              {emailSaving ? 'Saving...' : 'Save Email Config'}
            </button>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--text-secondary)' }}>Send Test Email</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={testEmailAddr}
                  onChange={e => setTestEmailAddr(e.target.value)}
                  placeholder="recipient@example.com"
                  style={{ flex: 1 }}
                  onKeyDown={e => e.key === 'Enter' && handleTestEmail(testEmailAddr)}
                />
                <button
                  onClick={() => handleTestEmail(testEmailAddr)}
                  disabled={testingEmail || !testEmailAddr}
                  className="btn btn-secondary flex items-center gap-1.5"
                  style={{ fontSize: 13, color: '#0369a1', whiteSpace: 'nowrap' }}
                >
                  <Send size={13} />{testingEmail ? 'Sending...' : 'Send Test'}
                </button>
              </div>
              <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>Sends a test email to verify your SendGrid configuration is working.</p>
            </div>
          </div>

          {/* SMS config */}
          <div className="card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Phone size={18} style={{ color: '#7c3aed' }} />
                <h3 className="font-semibold" style={{ color: 'var(--text)' }}>SMS Notifications (Twilio)</h3>
              </div>
              {deliveryConfig.twilio_configured
                ? <span className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: '#f0fdf4', color: '#16a34a' }}><CheckCircle2 size={12} /> Configured</span>
                : <span className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: '#fef2f2', color: '#dc2626' }}><AlertCircle size={12} /> Not configured</span>
              }
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                Account SID {deliveryConfig.twilio_configured && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(leave blank to keep existing)</span>}
              </label>
              <div className="relative">
                <input
                  type={showAccountSid ? 'text' : 'password'}
                  value={smsForm.account_sid}
                  onChange={e => setSmsForm(f => ({ ...f, account_sid: e.target.value }))}
                  placeholder={deliveryConfig.twilio_configured ? '••••••••••••••••••••' : 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
                  style={{ paddingRight: 40 }}
                />
                <button type="button" onClick={() => setShowAccountSid(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  {showAccountSid ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                Auth Token {deliveryConfig.twilio_configured && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(leave blank to keep existing)</span>}
              </label>
              <div className="relative">
                <input
                  type={showAuthToken ? 'text' : 'password'}
                  value={smsForm.auth_token}
                  onChange={e => setSmsForm(f => ({ ...f, auth_token: e.target.value }))}
                  placeholder={deliveryConfig.twilio_configured ? '••••••••••••••••••••' : 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
                  style={{ paddingRight: 40 }}
                />
                <button type="button" onClick={() => setShowAuthToken(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  {showAuthToken ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Twilio Phone Number</label>
              <input value={smsForm.phone_number} onChange={e => setSmsForm(f => ({ ...f, phone_number: e.target.value }))} placeholder="+61400000000" />
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Use E.164 format with country code (e.g. +61 for Australia)</p>
            </div>

            <button onClick={handleSaveSMSConfig} disabled={smsSaving} className="btn btn-primary" style={{ fontSize: 13 }}>
              {smsSaving ? 'Saving...' : 'Save SMS Config'}
            </button>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--text-secondary)' }}>Send Test SMS</label>
              <div className="flex gap-2">
                <input
                  value={testPhone}
                  onChange={e => setTestPhone(e.target.value)}
                  placeholder="+61400000000"
                  style={{ flex: 1 }}
                  onKeyDown={e => e.key === 'Enter' && handleTestSMS(testPhone)}
                />
                <button
                  onClick={() => handleTestSMS(testPhone)}
                  disabled={testingSMS || !testPhone}
                  className="btn btn-secondary flex items-center gap-1.5"
                  style={{ fontSize: 13, color: '#7c3aed', whiteSpace: 'nowrap' }}
                >
                  <Send size={13} />{testingSMS ? 'Sending...' : 'Send Test'}
                </button>
              </div>
              <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>Sends a test SMS to verify your Twilio configuration is working.</p>
            </div>
          </div>

          <div className="card p-4" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
            <p className="text-sm" style={{ color: '#92400e' }}>
              <strong>Security note:</strong> API keys and tokens are stored encrypted per organisation. Keys are never returned in API responses — only configuration status is shown.
              You can also test delivery for individual escalation contacts using the Test Email / Test SMS buttons in the Escalation Contacts tab.
            </p>
          </div>
        </div>
      )}

      {/* ── TECH ALERT MODAL ── */}
      {showTechModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden flex flex-col" style={{ background: 'var(--white)', maxHeight: '92vh' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-2.5">
                <Cpu size={18} style={{ color: '#374151' }} />
                <h2 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>
                  {editAlertId ? 'Modify & Resend Alert' : 'Send Technical Alert'}
                </h2>
              </div>
              <button onClick={() => setShowTechModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>

            <form onSubmit={handleTechSubmit} className="overflow-y-auto flex-1">
              <div className="px-6 py-5 space-y-5">
                {/* Title */}
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Alert Title *</label>
                  <input value={techForm.title} onChange={e => setTechForm((f: any) => ({ ...f, title: e.target.value }))} placeholder="e.g. Planned system maintenance — Saturday 6am–8am" required />
                </div>

                {/* Content mode */}
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--text-secondary)' }}>Alert Content</label>
                  <div className="flex gap-2 mb-3">
                    {([{ v: 'text', label: 'Free Text', icon: MessageSquare }, { v: 'doc', label: 'Document Upload', icon: Paperclip }] as const).map(opt => (
                      <button key={opt.v} type="button" onClick={() => setContentMode(opt.v)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all"
                        style={{ background: contentMode === opt.v ? 'var(--accent)' : 'white', color: contentMode === opt.v ? 'white' : 'var(--text-secondary)', borderColor: contentMode === opt.v ? 'var(--accent)' : 'var(--border)' }}>
                        <opt.icon size={14} /> {opt.label}
                      </button>
                    ))}
                  </div>

                  {contentMode === 'text' && (
                    <textarea rows={5} value={techForm.body} onChange={e => setTechForm((f: any) => ({ ...f, body: e.target.value }))}
                      placeholder="Write the alert message here — include impact, affected systems, timeline and any required actions." style={{ width: '100%', resize: 'vertical' }} />
                  )}
                  {contentMode === 'doc' && (
                    !techForm.document_data ? (
                      <label className="flex flex-col items-center gap-3 p-8 rounded-xl border-2 border-dashed cursor-pointer hover:bg-gray-50 transition-all" style={{ borderColor: 'var(--border)' }}>
                        <Paperclip size={28} style={{ color: 'var(--text-muted)' }} />
                        <div className="text-center">
                          <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Click to attach a document</p>
                          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>PDF, Word, Excel, image — any format</p>
                        </div>
                        <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} />
                      </label>
                    ) : (
                      <div className="flex items-center gap-3 p-4 rounded-xl border" style={{ borderColor: 'var(--border)', background: '#f8fafc' }}>
                        <FileText size={24} style={{ color: 'var(--accent)' }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{techForm.document_name}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Ready to attach</p>
                        </div>
                        <button type="button" onClick={clearFile} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
                      </div>
                    )
                  )}
                </div>

                {/* Channel selector */}
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--text-secondary)' }}>Send Via</label>
                  <div className="flex gap-2">
                    {([
                      { v: 'email',     label: 'Email only', icon: Mail },
                      { v: 'sms',       label: 'SMS only',   icon: Phone },
                      { v: 'email_sms', label: 'Email + SMS', icon: Send },
                    ] as const).map(opt => (
                      <button key={opt.v} type="button"
                        onClick={() => setTechForm((f: any) => ({ ...f, send_channels: opt.v }))}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all"
                        style={{
                          background: techForm.send_channels === opt.v ? 'var(--accent)' : 'white',
                          color: techForm.send_channels === opt.v ? 'white' : 'var(--text-secondary)',
                          borderColor: techForm.send_channels === opt.v ? 'var(--accent)' : 'var(--border)',
                        }}>
                        <opt.icon size={14} /> {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Recipient mode toggle */}
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--text-secondary)' }}>Send To</label>
                  <div className="flex gap-2 mb-4">
                    {([
                      { v: 'audience'   as RecipientMode, label: 'Broadcast to Group', icon: Users },
                      { v: 'individual' as RecipientMode, label: 'Select Individuals',  icon: UserCheck },
                    ]).map(opt => (
                      <button key={opt.v} type="button"
                        onClick={() => { setRecipientMode(opt.v); if (opt.v === 'individual') loadRecipientOptions() }}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all"
                        style={{ background: recipientMode === opt.v ? 'var(--accent)' : 'white', color: recipientMode === opt.v ? 'white' : 'var(--text-secondary)', borderColor: recipientMode === opt.v ? 'var(--accent)' : 'var(--border)' }}>
                        <opt.icon size={14} /> {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Audience broadcast selector */}
                  {recipientMode === 'audience' && (
                    <div className="grid grid-cols-2 gap-2">
                      {AUDIENCE_OPTIONS.map(opt => (
                        <label key={opt.value} className="flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all"
                          style={{ borderColor: techForm.audience === opt.value ? opt.color : 'var(--border)', background: techForm.audience === opt.value ? opt.bg : 'white' }}>
                          <input type="radio" name="audience" value={opt.value} checked={techForm.audience === opt.value}
                            onChange={() => setTechForm((f: any) => ({ ...f, audience: opt.value }))}
                            style={{ marginTop: 2, accentColor: opt.color }} />
                          <div>
                            <p className="text-sm font-semibold" style={{ color: techForm.audience === opt.value ? opt.color : 'var(--text)' }}>{opt.label}</p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{opt.desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Individual recipient picker */}
                  {recipientMode === 'individual' && (
                    <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                      {/* Selected chips */}
                      {selectedRecipients.length > 0 && (
                        <div className="px-3 py-2 flex flex-wrap gap-1.5" style={{ background: '#f0f9ff', borderBottom: '1px solid var(--border)' }}>
                          {selectedRecipients.map((r: any, i: number) => (
                            <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: 'white', border: '1px solid var(--border)', color: 'var(--text)' }}>
                              {r.type === 'user'    && <User    size={10} style={{ color: '#0369a1' }} />}
                              {r.type === 'contact' && <Bell    size={10} style={{ color: '#7c3aed' }} />}
                              {r.type === 'company' && <HardHat size={10} style={{ color: '#d97706' }} />}
                              {r.name}
                              <button type="button" onClick={() => toggleRecipient(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, lineHeight: 1 }}>
                                <X size={10} />
                              </button>
                            </span>
                          ))}
                          <span className="text-xs self-center" style={{ color: 'var(--text-muted)' }}>{selectedRecipients.length} selected</span>
                        </div>
                      )}

                      {/* Source tabs */}
                      <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
                        {([
                          { id: 'users'    as RecipientTab, label: 'Users',    icon: User,    count: recipientOptions.users.length },
                          { id: 'contacts' as RecipientTab, label: 'Contacts', icon: Bell,    count: recipientOptions.contacts.length },
                          { id: 'companies'as RecipientTab, label: 'Companies',icon: HardHat, count: recipientOptions.companies.length },
                        ]).map(t => (
                          <button key={t.id} type="button" onClick={() => setRecipientTab(t.id)}
                            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium flex-1 justify-center"
                            style={{ background: recipientTab === t.id ? 'var(--bg)' : 'white', borderBottom: `2px solid ${recipientTab === t.id ? 'var(--accent)' : 'transparent'}`, color: recipientTab === t.id ? 'var(--accent)' : 'var(--text-secondary)', border: 'none', cursor: 'pointer', borderBottomStyle: 'solid', borderBottomWidth: 2, borderBottomColor: recipientTab === t.id ? 'var(--accent)' : 'transparent' }}>
                            <t.icon size={13} /> {t.label}
                            {t.count > 0 && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: recipientTab === t.id ? 'var(--accent)' : '#e5e7eb', color: recipientTab === t.id ? 'white' : 'var(--text-muted)' }}>{t.count}</span>}
                          </button>
                        ))}
                      </div>

                      {/* Search */}
                      <div className="relative px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
                        <Search size={14} className="absolute left-5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                        <input value={recipientSearch} onChange={e => setRecipientSearch(e.target.value)}
                          placeholder={`Search ${recipientTab}...`} style={{ paddingLeft: 28, fontSize: 13 }} />
                      </div>

                      {/* List */}
                      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {loadingRecipients && <div className="p-4 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Loading...</div>}

                        {!loadingRecipients && recipientTab === 'users' && filteredUsers.map(u => (
                          <label key={u.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors" style={{ borderBottom: '1px solid var(--border)' }}>
                            <input type="checkbox" checked={isSelected(u.id, 'user')} onChange={() => toggleRecipient({ id: u.id, type: 'user', name: u.name, email: u.email, mobile: u.mobile })} style={{ width: 15, height: 15, accentColor: 'var(--accent)' }} />
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: 'var(--accent)' }}>
                              {(u.name || '?')[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{u.name}</div>
                              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{u.role?.replace('_', ' ')} {u.site_name ? `· ${u.site_name}` : ''}</div>
                            </div>
                            {u.email && <span className="text-xs truncate max-w-32" style={{ color: 'var(--text-muted)' }}>{u.email}</span>}
                          </label>
                        ))}

                        {!loadingRecipients && recipientTab === 'contacts' && filteredContacts.map(c => (
                          <label key={c.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors" style={{ borderBottom: '1px solid var(--border)' }}>
                            <input type="checkbox" checked={isSelected(c.id, 'contact')} onChange={() => toggleRecipient({ id: c.id, type: 'contact', name: c.name, email: c.email, mobile: c.mobile })} style={{ width: 15, height: 15, accentColor: '#7c3aed' }} />
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: '#7c3aed' }}>
                              {(c.name || '?')[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{c.name}</div>
                              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{c.role}</div>
                            </div>
                            {c.email && <span className="text-xs truncate max-w-32" style={{ color: 'var(--text-muted)' }}>{c.email}</span>}
                          </label>
                        ))}

                        {!loadingRecipients && recipientTab === 'companies' && filteredCompanies.map(co => (
                          <label key={co.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors" style={{ borderBottom: '1px solid var(--border)' }}>
                            <input type="checkbox" checked={isSelected(co.id, 'company')} onChange={() => toggleRecipient({ id: co.id, type: 'company', name: co.company_name, email: co.contact_email, mobile: co.contact_phone })} style={{ width: 15, height: 15, accentColor: '#d97706' }} />
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: '#d97706' }}>
                              {(co.company_name || '?')[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{co.company_name}</div>
                              {co.contact_name && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>c/o {co.contact_name}</div>}
                            </div>
                            {co.contact_email && <span className="text-xs truncate max-w-32" style={{ color: 'var(--text-muted)' }}>{co.contact_email}</span>}
                          </label>
                        ))}

                        {!loadingRecipients && (
                          (recipientTab === 'users' && filteredUsers.length === 0) ||
                          (recipientTab === 'contacts' && filteredContacts.length === 0) ||
                          (recipientTab === 'companies' && filteredCompanies.length === 0)
                        ) && (
                          <div className="p-4 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No {recipientTab} found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {techError && (
                  <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: '#fef2f2', color: 'var(--red)' }}>
                    <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{techError}
                  </div>
                )}
              </div>

              <div className="flex gap-3 px-6 py-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
                <button type="submit" className="btn btn-primary flex items-center gap-2" disabled={techSaving}>
                  {editAlertId ? <RotateCcw size={15} /> : <Send size={15} />}
                  {techSaving ? 'Sending...' : editAlertId ? 'Update & Resend' : 'Send Alert'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowTechModal(false)}>Cancel</button>
                {recipientMode === 'individual' && selectedRecipients.length > 0 && (
                  <span className="text-sm self-center ml-1" style={{ color: 'var(--text-muted)' }}>
                    {selectedRecipients.length} recipient{selectedRecipients.length !== 1 ? 's' : ''} selected
                  </span>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── RULE MODAL ── */}
      {showRuleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-xl rounded-2xl shadow-xl overflow-y-auto" style={{ background: 'var(--white)', maxHeight: '90vh' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10" style={{ borderColor: 'var(--border)' }}>
              <h2 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>{editRule ? 'Edit Alert Rule' : 'Add Alert Rule'}</h2>
              <button onClick={() => setShowRuleModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleRuleSubmit} className="px-6 py-5 space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Rule Name *</label>
                <input value={ruleForm.name} onChange={e => setRuleForm((f: any) => ({ ...f, name: e.target.value }))} placeholder="e.g. Fatality — Notify ELT immediately" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Category</label>
                  <select value={ruleForm.category} onChange={e => setRuleForm((f: any) => ({ ...f, category: e.target.value }))}>
                    <option value="regulatory">Regulatory Obligation</option>
                    <option value="elt">ELT Notification</option>
                    <option value="customer">Customer Notification</option>
                    <option value="business">Business Alert</option>
                    <option value="technical">Technical Alert</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Trigger Event</label>
                  <select value={ruleForm.trigger_event} onChange={e => setRuleForm((f: any) => ({ ...f, trigger_event: e.target.value }))}>
                    <option value="incident_created">Incident reported</option>
                    <option value="permit_expiring">Permit expiring</option>
                    <option value="corrective_action_overdue">Corrective action overdue</option>
                    <option value="competency_expiring">Competency expiring</option>
                    <option value="contractor_non_compliant">Contractor non-compliant</option>
                    <option value="audit_overdue">Audit overdue</option>
                    <option value="system_health">System health event</option>
                  </select>
                </div>
              </div>
              {ruleForm.trigger_event === 'incident_created' && (
                <>
                  <div>
                    <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>Applies to Incident Types</label>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(INCIDENT_TYPE_LABELS).map(([k, v]) => (
                        <label key={k} className="flex items-center gap-2 cursor-pointer text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <input type="checkbox" checked={ruleForm.incident_types.includes(k)}
                            onChange={e => setRuleForm((f: any) => ({ ...f, incident_types: e.target.checked ? [...f.incident_types, k] : f.incident_types.filter((x: string) => x !== k) }))}
                            style={{ width: 14, height: 14 }} /> {v}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Leave unchecked to apply to all types</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>Severity Levels</label>
                    <div className="flex gap-3">
                      {(['critical', 'high', 'medium', 'low']).map(s => (
                        <label key={s} className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" checked={ruleForm.severity_levels.includes(s)}
                            onChange={e => setRuleForm((f: any) => ({ ...f, severity_levels: e.target.checked ? [...f.severity_levels, s] : f.severity_levels.filter((x: string) => x !== s) }))}
                            style={{ width: 14, height: 14 }} />
                          <span className={`badge ${LEVEL_BADGE[s]} capitalize`} style={{ fontSize: 11 }}>{s}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Leave unchecked to apply to all severity levels</p>
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Notification Channel</label>
                  <select value={ruleForm.channel} onChange={e => setRuleForm((f: any) => ({ ...f, channel: e.target.value }))}>
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="email_sms">Email + SMS</option>
                    <option value="phone">Phone call</option>
                    <option value="written_report">Written report</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Timeframe (minutes)</label>
                  <input type="number" min="0" value={ruleForm.timeframe_minutes} onChange={e => setRuleForm((f: any) => ({ ...f, timeframe_minutes: parseInt(e.target.value) || 0 }))} />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{formatTimeframe(ruleForm.timeframe_minutes)} · 0 = immediate</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Recipient Type</label>
                  <select value={ruleForm.recipient_type} onChange={e => setRuleForm((f: any) => ({ ...f, recipient_type: e.target.value }))}>
                    <option value="regulator">Regulator</option>
                    <option value="elt">ELT / Executive</option>
                    <option value="customer">Customer</option>
                    <option value="contacts">Escalation Contacts</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Timeframe Source</label>
                  <select value={ruleForm.timeframe_source} onChange={e => setRuleForm((f: any) => ({ ...f, timeframe_source: e.target.value }))}>
                    <option value="regulatory">Regulatory (AU WHS)</option>
                    <option value="contractual">Contractual</option>
                    <option value="custom">Custom / Internal</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Recipient Name</label>
                <input value={ruleForm.recipient_name} onChange={e => setRuleForm((f: any) => ({ ...f, recipient_name: e.target.value }))} placeholder="e.g. SafeWork NSW, CEO, Acme Corp" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Regulation Reference</label>
                  <input value={ruleForm.regulation_ref} onChange={e => setRuleForm((f: any) => ({ ...f, regulation_ref: e.target.value }))} placeholder="e.g. WHS Act 2011 s38" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Jurisdiction</label>
                  <select value={ruleForm.jurisdiction} onChange={e => setRuleForm((f: any) => ({ ...f, jurisdiction: e.target.value }))}>
                    <option value="">All jurisdictions</option>
                    {['NSW','VIC','QLD','WA','SA','TAS','ACT','NT'].map(j => <option key={j} value={j}>{j}</option>)}
                  </select>
                </div>
              </div>
              {ruleError && (
                <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: '#fef2f2', color: 'var(--red)' }}>
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{ruleError}
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button type="submit" className="btn btn-primary" disabled={ruleSaving}>{ruleSaving ? 'Saving...' : editRule ? 'Save Changes' : 'Add Rule'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowRuleModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── CONTACT MODAL ── */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-lg rounded-2xl shadow-xl overflow-y-auto" style={{ background: 'var(--white)', maxHeight: '90vh' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10" style={{ borderColor: 'var(--border)' }}>
              <h2 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>{editContact ? 'Edit Contact' : 'Add Escalation Contact'}</h2>
              <button onClick={() => setShowContactModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleContactSubmit} className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Full Name *</label>
                  <input value={contactForm.name} onChange={e => setContactForm((f: any) => ({ ...f, name: e.target.value }))} placeholder="e.g. Jane Smith" required />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Role / Title</label>
                  <input value={contactForm.role} onChange={e => setContactForm((f: any) => ({ ...f, role: e.target.value }))} placeholder="e.g. Site Manager" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Escalation Order</label>
                  <input type="number" min="1" value={contactForm.escalation_order} onChange={e => setContactForm((f: any) => ({ ...f, escalation_order: parseInt(e.target.value) }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Email</label>
                  <input type="email" value={contactForm.email} onChange={e => setContactForm((f: any) => ({ ...f, email: e.target.value }))} placeholder="jane@company.com.au" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Mobile</label>
                  <input value={contactForm.mobile} onChange={e => setContactForm((f: any) => ({ ...f, mobile: e.target.value }))} placeholder="04XX XXX XXX" />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--text-secondary)' }}>Notify For Severity</label>
                <div className="grid grid-cols-2 gap-2">
                  {([{ key: 'notify_critical', label: 'Critical', badge: 'badge-critical' }, { key: 'notify_high', label: 'High', badge: 'badge-high' }, { key: 'notify_medium', label: 'Medium', badge: 'badge-medium' }, { key: 'notify_low', label: 'Low', badge: 'badge-low' }]).map(({ key, label, badge }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={contactForm[key]} onChange={e => setContactForm((f: any) => ({ ...f, [key]: e.target.checked }))} style={{ width: 15, height: 15 }} />
                      <span className={`badge ${badge}`}>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--text-secondary)' }}>Notification Methods</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={contactForm.method_email} onChange={e => setContactForm((f: any) => ({ ...f, method_email: e.target.checked }))} style={{ width: 15, height: 15 }} /> Email
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={contactForm.method_sms} onChange={e => setContactForm((f: any) => ({ ...f, method_sms: e.target.checked }))} style={{ width: 15, height: 15 }} /> SMS
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Response Window (minutes)</label>
                  <input type="number" min="5" value={contactForm.window_minutes} onChange={e => setContactForm((f: any) => ({ ...f, window_minutes: parseInt(e.target.value) }))} />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Time before escalating to next contact</p>
                </div>
                <div className="flex items-center gap-2 pt-5">
                  <input type="checkbox" id="biz-hrs" checked={contactForm.business_hours_only} onChange={e => setContactForm((f: any) => ({ ...f, business_hours_only: e.target.checked }))} style={{ width: 15, height: 15 }} />
                  <label htmlFor="biz-hrs" className="text-sm" style={{ color: 'var(--text-secondary)' }}>Business hours only</label>
                </div>
              </div>
              {contactError && (
                <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: '#fef2f2', color: 'var(--red)' }}>
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{contactError}
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button type="submit" className="btn btn-primary" disabled={contactSaving}>{contactSaving ? 'Saving...' : editContact ? 'Save Changes' : 'Add Contact'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowContactModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white" style={{ background: 'var(--green, #16a34a)', zIndex: 9999 }}>
          <Check size={16} />{toast}
        </div>
      )}
    </div>
  )
}
